import { AuthService } from '../../../auth/auth.service';
import { User } from './../../../_shared/models/user.model';
import { League } from '../../../_shared/models/league.model';
import { LeagueService } from './../../../leagues/league.service';
import { Location } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Subject, lastValueFrom } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DownloadTieService } from '../../../ties/tie-details/tie-scorecard/download-tie/download-tie.service';
import { LoaderService } from '../../../_core/services/loader.service';
import { SeasonSelectorService } from '../../../_shared/components/season-selector/season-selector.service';
import { Season } from '../../../_shared/models/season.model';
import { Team } from '../../../_shared/models/team.model';
import { Tie } from '../../../_shared/models/tie.model';
import { TeamDetailsService } from '../team-details.service';
import { TeamScheduleService } from './team-schedule.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    standalone: false,
    selector: 'app-team-schedule',
    templateUrl: './team-schedule.component.html',
    styleUrls: ['./team-schedule.component.scss'],
})
export class TeamScheduleComponent implements OnInit, OnDestroy {
    private authService = inject(AuthService);

    team: Team;
    isOwner: boolean;
    isOfficial: boolean;
    ties: Tie[];

    private readonly destroy$: Subject<void> = new Subject();
    isLoading: boolean = true;
    selectedSeason: Season;
    leagueId: string;
    isLeagueOwner: boolean;
    league: League;
    organisationId: string | null;

    constructor(
        private teamScheduleService: TeamScheduleService,
        private downloadTieService: DownloadTieService,
        private teamDetailsService: TeamDetailsService,
        private seasonSelectorService: SeasonSelectorService,
        private router: Router,
        private loaderService: LoaderService,
        private leagueService: LeagueService,
        private activatedRoute: ActivatedRoute,
        private translateService: TranslateService
    ) { }
    noTeamMatchesText = this.translateService.instant('You don\'t have any team matches');

    async ngOnInit() {
        this.isLoading = true;
        await this.getRouteSubscription();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async getRouteSubscription() {
        this.activatedRoute.parent?.params
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: async (params) => {
                    try {
                        const { id, leagueId } = params;
                        this.team = await this.teamDetailsService.info(id);
                        this.isOwner = this.teamDetailsService.isOwner(this.team.owners);
                        this.isOfficial = await this.teamDetailsService.isOfficial(this.team?.officials);
                        this.selectedSeason = this.seasonSelectorService.season;

                        if (leagueId) {
                            this.leagueId = leagueId;
                            await this.getLeague();
                        }
                    } catch (error) {
                        console.error('Error fetching route data:', error);
                        this.isLoading = false;
                    } finally {
                        this.loaderService.dismiss();
                        await this.subscribe();
                    }
                },
                error: (err) => {
                    console.error('Route subscription error:', err);
                    this.isLoading = false;
                    this.loaderService.dismiss();
                }
            });
    }

    async getTeamSchedule() {
        let partner = '';

        if (this.team?.organisations?.some(o => o.organisationAbbreviation === 'ITA')) {
            partner = 'ITA';
        } else if (this.team?.organisations?.some(o => o.organisationAbbreviation === 'USTA')) {
            partner = 'USTA';
        }

        // seasonStarting is using 2021, 2022 instead of seasonId.
        // Name of the season is changed to 2021/22... and the first 4 charactars are seasonStarting
        this.ties = (await lastValueFrom(this.teamScheduleService
            .getSchedule(this.team?.teamId, this.selectedSeason?.name?.substring(0, 4), partner, this.selectedSeason?._id)
            .pipe(
                takeUntil(this.destroy$),
            ))).data;
        this.isLoading = false;

        this.ties.sort((a, b) => {
            if (a.startDate > b.startDate) return 1;
            if (b.startDate > a.startDate) return -1;
            else return 0;
        });
    }

    async openTieDetails(index) {
        const tie = this.ties[index];

        if (!this.isLeagueOwner || tie.league || (this.isLeagueOwner && tie.matchStatus !== 'TBP')) {
            if (tie._id) {
                this.router.navigate(['/ties', this.ties[index]._id]);
            } else if (tie.score && tie.score !== '0 - 0') {
                await Browser.open({ url: `https://colleges.wearecollegetennis.com/vr?id=${this.team?.teamId}&a=Index&c=VenueTeam&s=/scorecard/${tie.tieId}` });
            }
        }
    }

    async subscribe() {
        this.downloadTieService
            .subscribeDownloadedTie()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (downloadedTie: Tie) => {
                    const index = this.ties.findIndex(tie => tie.tieId === downloadedTie.tieId);
                    this.ties[index] = downloadedTie;
                }
            );

        this.seasonSelectorService
            .subscribeSelectedSeason()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (season: Season) => {
                    if (season) {
                        this.selectedSeason = season;
                        this.getTeamSchedule();
                    }
                }
            );

        this.teamDetailsService.subscribeOrganisationId()
            .pipe(takeUntil(this.destroy$))
            .subscribe((id: string | null) => {
                this.organisationId = id;
                if (!id) {
                    this.getTeamSchedule();
                }
            })

    }

    private async getLeague() {
        this.league = (await lastValueFrom(this.leagueService
            .getLeague(this.leagueId, false)
            .pipe(
                takeUntil(this.destroy$)
            )))?.data

        this.isLeagueOwner = this.league.owners.some(owner => owner === this.authService.authUserSignal()?._id);
    }

}
